import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import ControlledTabs from 'src/components/controlled-tabs';
import { PageLayout } from 'src/layouts';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import useTabs from '@/hooks/useTabs';

const Page: FC = () => {
  const { t } = useTranslation(['common']);
  const title = t('risks.create_title');
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  // Published by the create form's `FormMenu` when it relocates behind this
  // toolbar. Null when it does not (flags off, or no permission to configure),
  // in which case the 3-dot survives on the form and the overflow item below is
  // correctly absent.
  const formConfig = useObjectToolbarFormStore((s) => s.formConfigState);
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: { title },
      save: true,
      actions: {
        overflow: formConfig
          ? [
              {
                id: 'edit',
                label: t('edit'),
                actions: [
                  {
                    id: 'edit-form',
                    label: t('edit_form'),
                    iconName: 'file-06' as const,
                    onClick: formConfig.openConfigure,
                  },
                ],
              },
            ]
          : [],
      },
    }),
    [title, t, formConfig]
  );
  const tabs = useTabs({
    parentType: Parent_Type_Enum.Risk,
    parent: undefined,
    hrefRoot: '/risks/create',
    disabled: true,
  });

  return (
    <PageLayout
      title={title}
      // Object-level toolbar (CUS-152) — flag-gated. Renders the title, the Save
      // disc / Cancel X (state published by the create form, RiskCreateTab →
      // RiskForm with `publishToolbarState`), and the relocated "Form settings"
      // overflow action. That action is not optional decoration: with the flag
      // on, `FormMenu` suppresses its own 3-dot, so without it "Configure form"
      // is unreachable on the create surface entirely (CUS-514).
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
    >
      <ControlledTabs
        tabs={tabs}
        variant={'container'}
        parentType={Parent_Type_Enum.Risk}
        disableSettings
      />
    </PageLayout>
  );
};

export default Page;
